import ErrorView from "@views/Error";
import { HomeSkeleton, LoginSkeleton } from "@ui/Skeleton";
import styles from "@styles/App.module.css";
import { hasRewardsData } from "@src/handlers/handleApi";
import { isInIframe } from "@src/utils/iframeResize";
import { type ReactNode, lazy, Suspense, useEffect, useState } from "react";
import {
  createHashRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  useLocation,
} from "react-router";
import { RouterProvider } from "react-router/dom";

const Login = lazy(() => import("@views/Login"));
const Home = lazy(() => import("@views/Home"));
const Maintenance = lazy(() => import("@views/Maintenance"));

function withSuspense(element: ReactNode, fallback: ReactNode) {
  return <Suspense fallback={fallback}>{element}</Suspense>;
}

const router = createHashRouter(
  createRoutesFromElements(
    <Route id="root" path="/" element={<Root />} errorElement={<ErrorView />}>
      <Route index element={withSuspense(<Login />, <LoginSkeleton />)} />
      <Route
        id="maintenance"
        path="maintenance"
        element={withSuspense(<Maintenance />, <LoginSkeleton />)}
      />
      <Route
        id="home"
        path="home"
        element={withSuspense(<Home />, <HomeSkeleton />)}
        errorElement={<ErrorView />}
      />
      <Route id="error" path="error" element={<ErrorView />} />
    </Route>,
  ),
);

function App() {
  useEffect(() => {
    if (isInIframe()) {
      document.documentElement.classList.add("iframe-embed");
    }
  }, []);

  return (
    <main id="ripcityrewards-app" className={styles.app}>
      <div className={styles.container}>
        <div className={styles.body}>
          <RouterProvider router={router} />
        </div>
      </div>
    </main>
  );
}

function Root() {
  const [hasAvailableRewards, setHasAvailableRewards] = useState<boolean>();
  const location = useLocation();

  useEffect(() => {
    void hasRewardsData().then(setHasAvailableRewards);
  }, []);

  if (location.pathname === "/maintenance") return <Outlet />;

  if (hasAvailableRewards === undefined) return <LoginSkeleton />;

  if (!hasAvailableRewards) return <Navigate to="/maintenance" replace />;

  return <Outlet />;
}

export default App;
